import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useJobs } from '../../context/JobContext';
import { useCompanyWorkForce } from '../../context/CompanyWorkForceContext';
import { useJobCategories } from '../../context/JobCategoryContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { formatCurrency } from '../../utils/format';
import { genRequest } from '../../utils/request';
import Breadcrumb from '../common/Breadcrumb';
import { useAuth } from '../../context/AuthContext';
import SendMailModal from '../common/SendMailModal';
  
import { 
  UserIcon,
  MapPinIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  TrophyIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ClockIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  ArrowLeftIcon,
  PrinterIcon,
  EllipsisVerticalIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  TruckIcon,
  DocumentDuplicateIcon,
  ShoppingBagIcon,
  PhotoIcon,
  EnvelopeIcon,
  DocumentCurrencyDollarIcon, 
  
} from '@heroicons/react/24/outline';

const JobView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getJobById} = useJobs();
  const { getWorkForce } = useCompanyWorkForce();
  const { getJobCategories } = useJobCategories();
  const { showLoading, hideLoading } = useLoading();
  const getRoutePath = useRoutePath();
  const { getToken } = useAuth();
  const [projectSpecsActiveTab, setProjectSpecsActiveTab] = useState('summary');
  const [jobActiveTab, setJobActiveTab] = useState('products');
  const [job, setJob] = useState(null);
  const [showSendMailModal, setShowSendMailModal] = useState(false);
 
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  
  // Additional data
  const [jobCategories, setJobCategories] = useState([]);
  
  const [assignedTechs, setAssignedTechs] = useState([]);
  
  // Refs to prevent API loops
  const hasFetchedJob = useRef(false);
  const hasFetchedAdditionalData = useRef(false);

  // Load estimation data
  const fetchJobData = useCallback(async () => {
    if (!id || hasFetchedJob.current) return;
    
    hasFetchedJob.current = true;
    showLoading();
    
    try {
      const response = await getJobById(id);
      if (response?.data) {
        setJob(response.data);
      }
    } catch (error) {
      console.error('Error fetching estimation data:', error);
    } finally {
      hideLoading();
      
    }
  }, [id, getJobById, showLoading, hideLoading, hasFetchedJob]);

  // Load additional data
  const fetchAdditionalData = useCallback(async () => {
    if (hasFetchedAdditionalData.current) return;
    
    hasFetchedAdditionalData.current = true;
    try {
      const [
        jobCategoriesData,
       
        workForceData,
        
      ] = await Promise.all([
        getJobCategories(0, { active: 1 }),
       
        getWorkForce(0, { active: 1, role: 'manager_supervisor' }),
      ]);

      setJobCategories(jobCategoriesData?.data || []);
     
      setAssignedTechs(workForceData?.data || []);
      
    } catch (error) {
      console.error('Error fetching additional data:', error);
    }
  }, [getJobCategories, getWorkForce]);

  useEffect(() => {
    if (id) {
      fetchJobData();
      fetchAdditionalData();
    }
  }, [id, fetchJobData, fetchAdditionalData]);

  // Close More menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreMenu && !event.target.closest('.more-menu-container')) {
        setShowMoreMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  const handleViewFile = (url) => {
    window.open(url, '_blank');
  };

  const handleDownloadFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Action button handlers
  const handleEdit = () => {
    navigate(getRoutePath(`/jobs/edit/${id}`));
  };
  
  const handlePrint = () => {
    handleDownloadPdf()
    //window.print();
  };

  const handleDownloadPdf = async () => {
    const data = {
      id: id,
      module: 'job',
      action:'pdf'
    };
    return await genRequest(data,getToken(),showLoading,hideLoading);
   
  };

  const handleDownloadExcel = async () => {
    const data = {
      id: id,
      module: 'job',
      action:'excel'
    };
    return await genRequest(data,getToken(),showLoading,hideLoading);
  };

  const handleDispatch = () => {
    // TODO: Implement dispatch functionality
    console.log('Dispatch clicked');
  };
 
  const handleCopyJob = () => {
    // TODO: Implement copy job functionality
    console.log('Copy job clicked');
  };

  const toggleMoreMenu = () => {
    setShowMoreMenu(!showMoreMenu);
  };

  const handleSendMail = () => {
    setShowSendMailModal(true);
  };

  const getTotals = () => {
    if (!job) return {};
    
    return {
      productsTotal: parseFloat(job.total_products_services_tax_fee) || 0,
      driverLaborTotal: parseFloat(job.total_driver_labor_time_cost) || 0,
      expensesTotal: parseFloat(job.total_billable_expenses_cost) || 0,
      jobTotal: parseFloat(job.job_total) || 0,
      paymentsTotal: parseFloat(job.total_payments_deposits_cost) || 0,
      totalDue: parseFloat(job.total_due) || 0,
      jobCost: parseFloat(job.job_cost) || 0,
      grossProfit: parseFloat(job.total_gross_profit_cost) || 0,
      grossProfitPercentage: parseFloat(job.total_gross_profit_percentage) || 0
    };
  };
 

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h2>
          <p className="text-gray-600 mb-6">The job you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate(getRoutePath('/jobs'))}
            className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  const totals = getTotals();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">

       {/* Header */}
        <div className="mb-8">
            <Breadcrumb />
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
                
              {/* Left side */}
              <div className="flex items-start md:items-center space-x-3">
                <div className="p-2 bg-[#009efb] rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                    Job Details
                    </h1>
                    {job?.estimation ? (
                      <span className="text-green-600">(<strong>Converted From Estimation</strong> #<Link target='_blank' to={getRoutePath(`/estimations/view/${job?.estimation?.id}`)}>{job?.estimation?.estimation_number}</Link>)</span>
                      ):<></>
                    }
                    <p class="text-gray-600 mt-1 text-sm md:text-base">Job #{job?.job_number}</p>
                </div>
                </div>

                {/* Right side buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
                <button
                    onClick={() => navigate(getRoutePath('/jobs'))}
                    className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors w-full sm:w-auto"
                >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back to List
                </button>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-2">

                {![3].includes(job?.get_status?.status_category_id) && (
                    <button
                        onClick={handleEdit}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors"
                    >
                        <PencilIcon className="h-4 w-4 mr-2" />
                        Edit
                    </button>
                )}
 
                    <button
                        onClick={handlePrint}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                        <PrinterIcon className="h-4 w-4 mr-2" />
                        Print
                    </button>

                    <button
                        onClick={handleSendMail}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        <EnvelopeIcon className="h-4 w-4 mr-2" />
                        Send Mail
                    </button>
                
                    {!job?.is_invoiced && (
                      <>
                      <Link
                        to={getRoutePath(`/invoices/create?jobId=${job?.job_number}`)}
                        target='_blank'
                        className="inline-flex items-center justify-center px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <DocumentCurrencyDollarIcon className="h-4 w-4 mr-2" /> Create Invoice
                      </Link>
                      </>
                    )}

                    {/* More Menu */}
                    <div className="relative">
                        <button
                        onClick={toggleMoreMenu}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                        >
                        <EllipsisVerticalIcon className="h-4 w-4" />
                        </button>

                        {showMoreMenu && (
                          <div className="more-menu-container absolute left-0 sm:left-auto sm:right-0 mt-2 w-48 max-w-[90vw] bg-white rounded-md shadow-lg border border-gray-200 z-50">
                            <div className="py-1 text-xs">
                              <button
                                onClick={() => {
                                  handleDownloadPdf();
                                  setShowMoreMenu(false)
                                }}
                                className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100"
                              >
                                <DocumentArrowDownIcon className="h-4 w-4 mr-3" />
                                Download PDF
                              </button>
                              <button
                                onClick={() => {
                                  handleDownloadExcel();
                                  setShowMoreMenu(false)
                                }}
                                className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100"
                              >
                                <TableCellsIcon className="h-4 w-4 mr-3" />
                                Download as Excel
                              </button>
                              <button
                                onClick={() => {
                                  handleDispatch();
                                  setShowMoreMenu(false)
                                }}
                                className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100"
                              >
                                <TruckIcon className="h-4 w-4 mr-3" />
                                Dispatch
                              </button>
                              <button
                                onClick={() => {
                                handleCopyJob();
                                  setShowMoreMenu(false)
                                }}
                                className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100"
                              >
                                <DocumentDuplicateIcon className="h-4 w-4 mr-3" />
                                Copy Job
                              </button>
                            </div>
                          </div>
                        )}
                    </div>
                </div>
  
              </div>
            </div>
        </div>
 
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Left Column - Customer & Contact Info */}
          <div className="space-y-6">
            {/* Project Specs Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Project Specs</h2>
                </div>
              </div>
              
              {/* Project Specs Tabs */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6 p-3 overflow-x-auto scrollbar-hide" aria-label="Tabs">
                  <button
                    type="button"
                    onClick={() => setProjectSpecsActiveTab('summary')}
                    className={`${
                      projectSpecsActiveTab === 'summary'
                        ? 'border-[#009efb] text-[#009efb]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                  >
                    Summary
                  </button>
                  <button
                    type="button"
                    onClick={() => setProjectSpecsActiveTab('pics')}
                    className={`${
                      projectSpecsActiveTab === 'pics'
                        ? 'border-[#009efb] text-[#009efb]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                  >
                    Pics
                  </button>
                  <button
                    type="button"
                    onClick={() => setProjectSpecsActiveTab('docs')}
                    className={`${
                      projectSpecsActiveTab === 'docs'
                        ? 'border-[#009efb] text-[#009efb]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                  >
                    Docs
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {/* Summary Tab Content */}
                {projectSpecsActiveTab === 'summary' && (
                  <div className="space-y-6">
                    {/* Customer Section */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">Customer</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-900">
                          {job.customer?.customerName || job.customer?.name || 'Unknown Customer'}
                        </span>
                      </div>
                    </div>

                    {/* Primary Contact Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900">Primary Contact</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <span className="text-gray-900">
                              {job.primary_contact?.first_name || 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <span className="text-gray-900">
                              {job.primary_contact?.last_name || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Phone Numbers */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">Phone Numbers</label>
                        {(job.primary_contact?.phone_numbers || []).map((phone, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <span className="text-sm text-gray-500">{phone.type}</span>
                              <div className="text-gray-900">
                                {phone.number} {phone.ext && `Ext: ${phone.ext}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Emails */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">Email Addresses</label>
                        {(job.primary_contact?.emails || []).map((email, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <span className="text-sm text-gray-500">{email.type}</span>
                              <div className="text-gray-900">{email.address}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Job Category */}
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Job Category</label>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-900">
                            {jobCategories.find(cat => cat.id === job.job_category_id)?.name || 'N/A'}
                          </span>
                        </div>
                      </div>

                       {/* Description */}
                       <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-900">
                            {job.description || 'N/A'}
                          </span>
                        </div>
                      </div>

                      {/* PO Number */}
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">PO #</label>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-900">
                            {job.po_number || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pics Tab Content */}
                {projectSpecsActiveTab === 'pics' && (
                  <div className="space-y-6">
                    {job.pictures && job.pictures.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {job.pictures.map((pic) => (
                          <div key={pic.id} className="relative group">
                            <img
                              src={pic.url}
                              alt={pic.name}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 hover:cursor-pointer bg-opacity-0 hover:rounded-lg bg-black/0  group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 flex space-x-2">
                                <button
                                  onClick={() => handleViewFile(pic.url)}
                                  className="p-2 bg-white rounded-full hover:bg-gray-100"
                                >
                                  <EyeIcon className="h-4 w-4 text-gray-700" />
                                </button>
                                <button
                                  onClick={() => handleDownloadFile(pic.url, pic.name)}
                                  className="p-2 bg-white rounded-full hover:bg-gray-100"
                                >
                                  <ArrowDownTrayIcon className="h-4 w-4 text-gray-700" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center space-y-4 mt-3">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                            <PhotoIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <div className='text-center'>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pictures Uploaded</h3>
                            
                            </div>
                        </div>
                     
                    )}
                  </div>
                )}

                {/* Docs Tab Content */}
                {projectSpecsActiveTab === 'docs' && (
                  <div className="space-y-6">
                    {(job.documents && job.documents.length > 0) || (job.selected_docs && job.selected_docs.length > 0) ? (
                      <div className="space-y-4">
                        {job.documents && job.documents.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <div className="text-gray-900 font-medium truncate">{doc.name}</div>
                              <div className="text-sm text-gray-500">{doc.created_at}</div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewFile(doc.url)}
                                className="text-[#009efb] hover:text-[#0089db]"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadFile(doc.url, doc.name)}
                                className="text-green-600 hover:text-green-700"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {job.selected_docs && job.selected_docs.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <div className="text-gray-900 font-medium truncate">{doc.name}</div>
                              <div className="text-sm text-gray-500">Customer Document</div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewFile(doc.url)}
                                className="text-[#009efb] hover:text-[#0089db]"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadFile(doc.url, doc.name)}
                                className="text-green-600 hover:text-green-700"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center space-y-4 mt-3">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                            <DocumentTextIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <div className='text-center'>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Uploaded</h3>
                            
                            </div>
                        </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Service Locations Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Service Locations</h2>
                </div>
              </div>
              <div className="p-6">
                {(job.service_locations || []).map((location, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-4">Location {index + 1}</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Location Nickname</label>
                        <div className="p-2 bg-gray-50 rounded-md">
                          <span className="text-gray-900">{location.location_nickname || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                        <div className="p-2 bg-gray-50 rounded-md">
                          <span className="text-gray-900">{location.street_address || 'N/A'}</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Apt/Suite/Unit</label>
                        <div className="p-2 bg-gray-50 rounded-md">
                          <span className="text-gray-900">{location.apt || 'N/A'}</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <div className="p-2 bg-gray-50 rounded-md">
                          <span className="text-gray-900">{location.city || 'N/A'}</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <div className="p-2 bg-gray-50 rounded-md">
                          <span className="text-gray-900">{location.state || 'N/A'}</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                        <div className="p-2 bg-gray-50 rounded-md">
                          <span className="text-gray-900">{location.zipcode || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={location.is_primary_location || false}
                          disabled
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">Primary Location</label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={location.is_billing_address || false}
                          disabled
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">Billing Address</label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={location.is_gated_property || false}
                          disabled
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">Gated Property</label>
                      </div>

                      {location.gate_access_instructions && (
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Gate Access Instructions</label>
                          <div className="p-2 bg-gray-50 rounded-md">
                            <span className="text-gray-900">{location.gate_access_instructions}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Sales Data & Actions */}
          <div className="space-y-6">
            {/* Sales Data Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Sales Data</h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {/* Requested On */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Requested On
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-900">{job.requested_on || 'N/A'}</span>
                  </div>
                </div>

                {/* Current Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <TrophyIcon className="h-4 w-4 mr-2" />
                   
                    Current Status
                  </label>
                   
                   
                  <div className="inline-flex items-center gap-2 text-xs leading-5 font-semibold">
  
                     
                    {job?.invoice?.is_full_paid === 1 && (
                      <div className="px-2 rounded-full bg-green-100 text-green-700 border border-green-200 flex items-center gap-1">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        <span>Paid in Full</span>
                      </div>
                    )}

                    <div
                      className="px-2 rounded-full"
                      style={job?.job_status?.style}
                    >
                      {job?.job_status?.name}
                    </div>

                  </div>

                  {!!job?.is_invoiced && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3  mt-2 rounded-lg">
                      
                      {/* Invoice details */}
                      <div className="flex flex-wrap items-center gap-2 text-sm border border-gray-200 rounded-lg p-2 w-full sm:w-auto">
                        <span className="whitespace-nowrap font-medium">Invoice#:</span>
                        <Link
                          target="_blank"
                          to={`/invoices/view/${job?.invoice?.id}`}
                          className="text-[#009efb] hover:text-[#0089db] break-words"
                        >
                          {job?.invoice?.invoice_number}
                        </Link>
                        <span className="text-gray-600 break-words">- {job?.invoice?.formated_invoice_date}</span>
                      </div>
                    </div>
                  )}
 
                </div>
 
                {/* Assigned Technicians */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    Assigned Technicians
                  </label>
                   
                  <div className="flex flex-wrap gap-2">
                    {(job.assigned_tech_ids || []).map((techId) => {
                      const tech = assignedTechs.find(t => t.id === techId);
                      return tech ? (
                        <span key={techId} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {tech.first_name} {tech.last_name}
                        </span>
                      ) : null;
                    })}
                    {(!job.assigned_tech_ids || job.assigned_tech_ids.length === 0) && (
                      <span className="text-gray-500">No technicians assigned</span>
                    )}
                  </div>
                </div>

                {/* Start & End Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    Start & End Date
                  </label>
                  <div className="space-y-2">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-500">Start & End Date</span>
                      <div className="text-gray-900">
                        {job?.start_date ? job.start_date : ""} 
                        {job?.end_date ? `- ${job.end_date}` : ""}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-500">Arrival Start</span>
                        <div className="text-gray-900">{job.arrival_start || 'N/A'}</div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-500">Arrival End</span>
                        <div className="text-gray-900">{job.arrival_end || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <div className="space-y-2">

                      {/* Note for Tech */}
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-500">Notes for Techs</span>
                        <div className="text-gray-900">
                          {job?.note_for_tech || "-"}
                        </div>
                      </div>

                      {/* Note to Customer */}
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-500">Note to Customer</span>
                        <div className="text-gray-900">
                          {job?.note_to_customer || "-"}
                        </div>
                      </div>

                       {/* Additional Site Visits */}
                     <div>
                      <div className="flex items-center justify-between mb-3 mt-3">
                        <label className="block text-sm font-medium text-gray-700 flex items-center">
                          
                          Additional Site Visits ({job?.site_visits?.length || 0})
                        </label>
                      </div>
 
                      {/* Site Visit Modal */}
                        {job?.site_visits?.length > 0 ? (
                          <div className="mt-3 space-y-3">
                           
                            {job?.site_visits?.map((siteVisit, index) => (
                              <div key={siteVisit.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Visit {index + 1}</h4>
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-4 mb-2">
                                      <div className="flex items-center gap-2">
                                        <CalendarIcon className="h-4 w-4 text-gray-500" />
                                        <span className="text-sm font-medium text-gray-900">
                                          {siteVisit.onsite_visit_date || siteVisit.start_date}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <ClockIcon className="h-4 w-4 text-gray-500" />
                                        <span className="text-sm text-gray-600">
                                          {siteVisit.arrival_start} - {siteVisit.arrival_end}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {siteVisit.duration_h || siteVisit.duration_m ? (
                                      <div className="flex items-center gap-2 mb-2">
                                        <ClockIcon className="h-4 w-4 text-gray-500" />
                                        <span className="text-sm text-gray-600">
                                          Duration: {siteVisit.duration_h || 0}h {siteVisit.duration_m || 0}m
                                        </span>
                                      </div>
                                    ) : null}
                                    
                                    {siteVisit.assigned_tech_ids && siteVisit.assigned_tech_ids.length > 0 ? (
                                      <div className="flex items-center gap-2 mb-2">
                                        <UserGroupIcon className="h-4 w-4 text-gray-500" />
                                        <span className="text-sm text-gray-600">
                                          Techs: {siteVisit.assigned_techs?.map((tech) => tech.first_name + ' ' + tech.last_name).join(', ')}
                                        </span>
                                      </div>
                                    ) : null}
                                    
                                    {siteVisit.note_for_tech && (
                                      <div className="flex items-start gap-2">
                                        <DocumentTextIcon className="h-4 w-4 text-gray-500 mt-0.5" />
                                        <span className="text-sm text-gray-600">
                                          {siteVisit.note_for_tech}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                   
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-3 text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
                            <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">No site visits added yet</p>
                             
                          </div>
                        )}
                    </div>

                      {/* Completion Note */}
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-500">Completion Notes</span>
                        <div className="text-gray-900">
                          {job?.completion_note || "-"}
                        </div>
                      </div>

                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Job Section - Full Width */}
        <div className="mt-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 text-white mr-3" />
                <h2 className="text-xl font-semibold text-white">Job</h2>
              </div>
            </div>
            
            {/* Job Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6 p-3 overflow-x-auto scrollbar-hide" aria-label="Tabs">
                <button
                  type="button"
                  onClick={() => setJobActiveTab('products')}
                  className={`${
                    jobActiveTab === 'products'
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                >
                  Product & Services
                </button>
                <button
                  type="button"
                  onClick={() => setJobActiveTab('driver_labor')}
                  className={`${
                    jobActiveTab === 'driver_labor'
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                >
                  Driver & Labor Times
                </button>
                <button
                  type="button"
                  onClick={() => setJobActiveTab('expenses')}
                  className={`${
                    jobActiveTab === 'expenses'
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                >
                  Expenses
                </button>
              </nav>
            </div>

            <div className="p-6">
              {/* Products & Services Tab */}
              {jobActiveTab === 'products' && (
                <div>
                  {(job.products || []).length > 0 ? (
                    <div className="space-y-4">
                      {job.products.map((item, index) => (
                        <div key={item.id || index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-medium text-gray-900">{item.description}</h4>
                            <span className="text-lg font-semibold text-gray-900">
                              {formatCurrency(item.total || 0)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Quantity:</span>
                              <div className="text-gray-900">{item.quantity}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Rate:</span>
                              <div className="text-gray-900">{formatCurrency(item.rate || 0)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Cost:</span>
                              <div className="text-gray-900">{formatCurrency(item.cost || 0)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Margin:</span>
                              <div className="text-gray-900">{item.margin || 0}%</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center space-y-4 mt-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <ShoppingBagIcon  className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className='text-center'>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Line Items Found</h3>
                         
                        </div>
                    </div>
                  )}
                </div>
              )}

              {/* Driver & Labor Times Tab */}
            {jobActiveTab === 'driver_labor' && (
                <div>
                  {(job.driver_labor_times || []).length > 0 ? (
                    <div className="space-y-4">
                      {job.driver_labor_times.map((item, index) => (
                        <div key={item.id || index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-medium text-gray-900">
                            {item?.tech?.first_name || item?.tech?.last_name
                                 ? `${item?.tech?.first_name || ''} ${item?.tech?.last_name || ''}`
                                 : 'Unknown'} - {item?.formated_date || 'N/A'}
                            </h4>
                            <span className="text-lg font-semibold text-gray-900">
                              {formatCurrency(item.total || 0)}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Drive Time:</span>
                              <div className="text-gray-900">
                                {item.driveStartTime} - {item.driveEndTime} ({item.driverHours}h {item.driverMinutes}m)
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-500">Labor Time:</span>
                              <div className="text-gray-900">
                                {item.laborStartTime} - {item.laborEndTime} ({item.laborHours}h {item.laborMinutes}m)
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-500">Driver Rate:</span>
                              <div className="text-gray-900">{formatCurrency(item.driverRate || 0)}/hr</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Labor Rate:</span>
                              <div className="text-gray-900">{formatCurrency(item.laborRate || 0)}/hr</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Driver Total:</span>
                              <div className="text-gray-900">{formatCurrency(item.driverTotal || 0)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Labor Total:</span>
                              <div className="text-gray-900">{formatCurrency(item.laborTotal || 0)}</div>
                            </div>
                            <div className="md:col-span-2">
                              <span className="text-gray-500">Billable:</span>
                              <div className="text-gray-900">
                                Driver: {item.driverBillable ? 'Yes' : 'No'} | Labor: {item.laborBillable ? 'Yes' : 'No'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (

                    <div className="flex flex-col items-center justify-center space-y-4 mt-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <UserIcon  className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className='text-center'>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Driver/Labor Times Found</h3>
                         
                        </div>
                    </div>
 
                  )}
                </div>
              )}

              {/* Expenses Tab */}
              {jobActiveTab === 'expenses' && (
                <div>
                  {(job.expenses || []).length > 0 ? (
                    <div className="space-y-4">
                      {job.expenses.map((item, index) => (
                        <div key={item.id || index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-medium text-gray-900">{item.purchased_from || 'Unknown Vendor'}</h4>
                            <span className="text-lg font-semibold text-gray-900">
                              {formatCurrency(item.amount || 0)}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <div className="text-gray-900">{item.date || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Category:</span>
                              <div className="text-gray-900">
                                {item?.category?.name || 'N/A'}
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-500">Billable:</span>
                              <div className="text-gray-900">{item.billable ? 'Yes' : 'No'}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Reimburse To:</span>
                              <div className="text-gray-900">{item?.reimburse_to_user?.first_name + ' ' + item?.reimburse_to_user?.last_name || 'N/A'}</div>
                            </div>
                            {item.notes && (
                              <div className="md:col-span-2">
                                <span className="text-gray-500">Notes:</span>
                                <div className="text-gray-900">{item.notes}</div>
                              </div>
                            )}
                          </div>
                           
                         
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <h5 className="text-sm font-medium text-gray-700 mb-3">Receipts</h5>
                              <div className="grid grid-cols-1 gap-3">
                                  
                                {/* Receipts */}
                               
                                {item?.receipts?.flat()?.length > 0 ? (
                                    item.receipts.flat().map((receipt, receiptIndex) => (
                                        <div
                                        key={`receipt-${receiptIndex}`}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                        >
                                        <div className="flex-1 min-w-0">
                                            <div className="text-gray-900 font-medium truncate">
                                            {receipt.name || receipt.filename || 'Receipt'}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                            {receipt.created_at || receipt.uploaded_at || 'N/A'}
                                            </div>
                                        </div>
                                        <div className="flex space-x-2 ml-3">
                                            <button
                                            onClick={() => handleViewFile(receipt.url || receipt.file_url)}
                                            className="text-[#009efb] hover:text-[#0089db] p-1"
                                            title="View receipt"
                                            >
                                            <EyeIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                            onClick={() =>
                                                handleDownloadFile(
                                                receipt.url || receipt.file_url,
                                                receipt.name || receipt.filename || 'receipt'
                                                )
                                            }
                                            className="text-green-600 hover:text-green-700 p-1"
                                            title="Download receipt"
                                            >
                                            <ArrowDownTrayIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                        </div>
                                    ))
                                    ) : (
                                    <div className="text-gray-500 text-sm italic">No Receipt Found</div>
                                    )}
  
                              </div>
                            </div>
                          
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center space-y-4 mt-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <CurrencyDollarIcon  className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className='text-center'>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Expenses Found</h3>
                        
                        </div>
                    </div>
                    
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Totals Summary Section - Full Width */}
        <div className="mt-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Totals Summary</h2>
                </div>
              </div>
            </div>
             <div className="p-6">
               <div className="space-y-3">
                 <div className="flex justify-between">
                   <span className="text-gray-600">Products Total:</span>
                   <span className="font-semibold">{formatCurrency(totals.productsTotal)}</span>
                 </div>
                 <div className="flex justify-between">
                   <span className="text-gray-600">Driver/Labor Total:</span>
                   <span className="font-semibold">{formatCurrency(totals.driverLaborTotal)}</span>
                 </div>
                 <div className="flex justify-between">
                   <span className="text-gray-600">Expenses Total:</span>
                   <span className="font-semibold">{formatCurrency(totals.expensesTotal)}</span>
                 </div>
                                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Job Total:</span>
                    <span>{formatCurrency(totals.jobTotal)}</span>
                  </div>
                   
                   <div className="flex justify-between text-sm">
                     <span className="text-gray-600">Payments/Deposits:</span>
                     <span className="font-semibold">{formatCurrency(totals.paymentsTotal)}</span>
                   </div>
                   
                   <div className="flex justify-between text-lg font-bold border-t pt-2">
                     <span>Total Due:</span>
                     {formatCurrency(totals.totalDue)}
                   </div>
                   
                                        {/* Job Cost Section */}
                   <div className="border-t pt-3 mt-3">
                     <div className="text-sm font-medium text-gray-700 mb-2">Job Cost Breakdown:</div>
                     <div className="space-y-1 text-sm">
                       <div className="flex justify-between text-base font-bold border-t pt-1">
                         <span>Total Job Cost:</span>
                         {formatCurrency(totals.jobCost)}
                       </div>
                     </div>
                   </div>
                   
                   {/* Gross Profit Section */}
                   <div className="border-t pt-3 mt-3">
                     <div className="text-sm font-medium text-gray-700 mb-2">Profit Analysis:</div>
                     <div className="space-y-1 text-sm">
                       <div className="flex justify-between">
                         <span className="text-gray-600">Job Total:</span>
                         <span className="font-semibold">{formatCurrency(totals.jobTotal)}</span>
                       </div>
                       <div className="flex justify-between">
                         <span className="text-gray-600">Job Cost:</span>
                         <span className="font-semibold">{formatCurrency(totals.jobCost)}</span>
                       </div>
                       <div className="flex justify-between text-base font-bold border-t pt-1">
                         <span>Gross Profit ({totals.grossProfitPercentage}%)  :</span>
                         {formatCurrency(totals.grossProfit,true)}
                       </div>
                        
                     </div>
                   </div>
               </div>
             </div>
          </div>
        </div>
      </div>
       {/* Send Mail Modal */}
       <SendMailModal
        isOpen={showSendMailModal}
        onClose={() => setShowSendMailModal(false)}
        moduleData={job}
        module="job"
        
      />
    </div>
  );
};

export default JobView;
